<template>
  <!-- Loading -->
  <div v-if="isLoadingDetails" class="has-text-centered py-6">
    <span class="icon is-large"><i class="fa fa-spinner fa-spin fa-2x" /></span>
    <p class="mt-2">Loading ontology details from OLS…</p>
  </div>

  <!-- Error -->
  <div
    v-else-if="loadError"
    class="notification is-danger is-light has-text-centered"
  >
    <span class="icon"><i class="fa fa-exclamation-circle" /></span>
    <span>{{ loadError }}</span>
  </div>

  <template v-else-if="ontology">
    <!-- OLS source card -->
    <div class="ols-card mb-5">
      <div class="ols-card__header">
        <span class="icon mb-1"><i class="fa fa-book" /></span>
        <h1 class="title is-4 mb-0">
          {{ ontology.name || ontology.ontology_id }}
        </h1>
      </div>
      <div class="ols-card__meta">
        <span class="tag is-info is-light">{{ ontology.ontology_id }}</span>
        <span v-if="ontology.number_of_terms" class="ols-card__chip">
          <i class="fa fa-tags mr-1" />
          {{ ontology.number_of_terms }} terms
        </span>
        <span v-if="ontology.version" class="ols-card__chip">
          v{{ ontology.version }}
        </span>
        <a
          v-if="ontology.homepage"
          :href="ontology.homepage"
          target="_blank"
          rel="noopener"
          class="ols-card__chip ols-card__chip--link"
        >
          <i class="fa fa-home mr-1" />
          Homepage
        </a>
        <a
          :href="`https://www.ebi.ac.uk/ols4/ontologies/${ontology.ontology_id}`"
          target="_blank"
          rel="noopener"
          class="ols-card__chip ols-card__chip--link"
        >
          <i class="fa fa-external-link mr-1" />
          OLS page
        </a>
      </div>
      <p v-if="ontology.description" class="ols-card__desc">
        {{ ontology.description }}
      </p>
    </div>

    <!-- Assign categories -->
    <h3 class="title is-5 mb-4">Assign Categories</h3>

    <!-- Default category -->
    <div class="mb-5">
      <p class="label">
        Default Category
        <span class="has-text-danger">*</span>
      </p>
      <div class="field">
        <label class="radio">
          <input
            v-model="categoryMode"
            type="radio"
            name="categoryMode"
            value="link"
          />
          Link to an existing category
        </label>
      </div>
      <div v-if="categoryMode === 'link'" class="field mb-3">
        <div class="control">
          <div class="select is-small" :class="{ 'is-danger': selectError }">
            <select
              v-model="selectedCategoryId"
              :class="{ 'is-danger': selectError }"
            >
              <option value="">— Select a category —</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                {{ cat.choice_label || cat.name }}
                <template v-if="cat.name">[{{ cat.name }}]</template>
              </option>
            </select>
          </div>
        </div>
        <p v-if="selectError" class="help is-danger">
          Please select a category.
        </p>
      </div>
      <div class="field">
        <label class="radio">
          <input
            v-model="categoryMode"
            type="radio"
            name="categoryMode"
            value="create"
          />
          Create a new category
        </label>
      </div>
      <div v-if="categoryMode === 'create'" class="field mb-3">
        <div class="control">
          <input
            v-model="newCategoryName"
            class="input is-small"
            :class="{ 'is-danger': nameError }"
            type="text"
            placeholder="Category name"
          />
        </div>
        <p v-if="nameError" class="help is-danger">
          Please enter a category name.
        </p>
      </div>
    </div>

    <!-- Additional categories -->
    <div
      v-if="availableAdditionalCategories.length > 0"
      class="ols-toggle-wrap mb-5"
    >
      <ToggleSection :collapsed="true">
        <template #label>
          Additional Categories
          <span class="has-text-grey is-size-7 ml-1">(optional)</span>
          <span
            v-if="additionalCategoryIds.length > 0"
            class="tag is-link is-light ml-2"
          >
            {{ additionalCategoryIds.length }} selected
          </span>
        </template>
        <div class="ols-additional-content">
          <p class="is-size-7 has-text-grey mb-3">
            Optionally link this ontology to more categories.
          </p>
          <div
            v-for="cat in availableAdditionalCategories"
            :key="cat.id"
            class="field"
          >
            <label class="checkbox">
              <input
                type="checkbox"
                :value="cat.id"
                :checked="additionalCategoryIds.includes(cat.id)"
                @change="toggleAdditionalCategory(cat.id)"
              />
              {{ cat.choice_label || cat.name }}
              <template v-if="cat.name">[{{ cat.name }}]</template>
            </label>
          </div>
        </div>
      </ToggleSection>
    </div>

    <!-- Actions -->
    <ProgressBar v-if="isImporting" class="mb-4" />
    <div class="ols-import__actions">
      <p class="has-text-grey is-size-7">
        Only ontology metadata is imported. Individual terms are stored only
        when you link them to vocabulary terms.
      </p>
      <BaseButton
        size="small"
        variant="primary"
        :loading="isImporting"
        :disabled="isImporting || !isImportReady"
        :icon="['fa', 'fa-download']"
        label="Import"
        @click="doImport"
      />
    </div>
  </template>
</template>

<script>
import { computed, onMounted, ref } from 'vue'

import { get, post } from '@/api'
import { useStore } from '@/store'
import { getErrorMessage } from '@/utils'

import ToggleSection from '@/components/fields/ToggleSection.vue'

export default {
  name: 'OlsOntologyImportForm',
  components: { ToggleSection },
  props: {
    ontologyId: { type: String, required: true },
    // Category to pre-select as default (e.g. when arriving via
    // "add ontology source" from that category)
    preselectCategoryId: { type: String, default: '' },
  },
  emits: ['imported'],
  setup(props, { emit }) {
    const store = useStore()

    const ontology = ref(null)
    const isLoadingDetails = ref(false)
    const loadError = ref('')
    const categories = ref([])
    const categoryMode = ref('link')
    const selectedCategoryId = ref('')
    const newCategoryName = ref('')
    const additionalCategoryIds = ref([])
    const isImporting = ref(false)

    const availableAdditionalCategories = computed(() =>
      categories.value.filter(
        (cat) => String(cat.id) !== String(selectedCategoryId.value),
      ),
    )
    const selectError = computed(
      () => categoryMode.value === 'link' && !selectedCategoryId.value,
    )
    const nameError = computed(
      () => categoryMode.value === 'create' && !newCategoryName.value.trim(),
    )
    const isImportReady = computed(() => {
      if (categoryMode.value === 'link') return !!selectedCategoryId.value
      if (categoryMode.value === 'create') return !!newCategoryName.value.trim()
      return false
    })

    function toggleAdditionalCategory(catId) {
      const idx = additionalCategoryIds.value.indexOf(catId)
      if (idx >= 0) additionalCategoryIds.value.splice(idx, 1)
      else additionalCategoryIds.value.push(catId)
    }

    async function loadOntologyDetails() {
      loadError.value = ''
      try {
        const { status, data } = await get(
          'vocabularies/ontologies/available-ontologies/',
          {
            params: { q: props.ontologyId, size: 50 },
          },
        )
        if (status === 200) {
          const match = (data.ontologies || []).find(
            (o) =>
              o.ontology_id?.toLowerCase() === props.ontologyId.toLowerCase(),
          )
          if (match) {
            ontology.value = match
            newCategoryName.value = match.name || match.ontology_id || ''
          } else {
            loadError.value = `Ontology '${props.ontologyId}' not found in OLS.`
          }
        }
      } catch (err) {
        loadError.value = getErrorMessage(
          err,
          'Failed to fetch ontology details from OLS.',
        )
      }
    }

    async function loadCategories() {
      try {
        const { status, data } = await get(
          'vocabularies/categories/?response_format=flat&page_size=max',
        )
        if (status === 200) {
          const raw = data.results || data || []
          categories.value = raw.sort((a, b) =>
            (a.choice_label || a.name || '').localeCompare(
              b.choice_label || b.name || '',
            ),
          )
        }
      } catch {
        // silent — category select stays empty
      }
    }

    async function doImport() {
      if (!ontology.value || !isImportReady.value) return
      isImporting.value = true
      try {
        const payload = { ontology_id: ontology.value.ontology_id }
        if (categoryMode.value === 'link') {
          payload.default_category_id = selectedCategoryId.value
        } else if (categoryMode.value === 'create') {
          payload.default_category_id = null
          payload.category_name = newCategoryName.value.trim()
        }
        if (additionalCategoryIds.value.length > 0) {
          payload.additional_category_ids = additionalCategoryIds.value
        }
        const { status } = await post(
          'vocabularies/ontologies/import-from-ols/',
          { data: payload },
        )
        if (status === 200 || status === 201) {
          store.commit('notify', {
            type: 'success',
            message: `Ontology "${ontology.value.name || ontology.value.ontology_id}" imported successfully`,
          })
          emit('imported', ontology.value)
        }
      } catch (err) {
        store.commit('notify', {
          type: 'error',
          message: getErrorMessage(err, 'Failed to import ontology from OLS'),
        })
      } finally {
        isImporting.value = false
      }
    }

    onMounted(async () => {
      isLoadingDetails.value = true
      try {
        await Promise.all([loadOntologyDetails(), loadCategories()])
        if (
          props.preselectCategoryId &&
          categories.value.some(
            (cat) => String(cat.id) === String(props.preselectCategoryId),
          )
        ) {
          categoryMode.value = 'link'
          selectedCategoryId.value = props.preselectCategoryId
        }
      } finally {
        isLoadingDetails.value = false
      }
    })

    return {
      ontology,
      isLoadingDetails,
      loadError,
      categories,
      categoryMode,
      selectedCategoryId,
      newCategoryName,
      additionalCategoryIds,
      availableAdditionalCategories,
      isImporting,
      isImportReady,
      selectError,
      nameError,
      toggleAdditionalCategory,
      doImport,
    }
  },
}
</script>

<style lang="scss" scoped>
.ols-card {
  padding: var(--space-4);
  background: var(--bg-default);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
}

.ols-card__header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: var(--space-3);
}

.ols-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.ols-card__chip {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  color: var(--text-light);

  &--link {
    color: var(--link);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
}

.ols-card__desc {
  font-size: 0.85rem;
  color: var(--text-light);
  line-height: 1.5;
  margin: 0;
}

.ols-toggle-wrap {
  border-radius: var(--radius-md);
  overflow: hidden;

  :deep(.panel-heading) {
    background: transparent !important;
  }
  :deep(nav.panel.is-opened .panel-heading) {
    background: var(--bg-subtle) !important;
  }
  &:has(nav.panel.is-opened) {
    border: 1px solid var(--border-subtle);
  }
  :deep(nav.panel.is-opened > .panel-block) {
    padding-bottom: 0;
  }
}

.ols-additional-content {
  max-height: 320px;
  overflow-y: auto;
  padding: var(--space-3) var(--space-4);

  .field {
    margin-bottom: 0;
  }

  label.checkbox {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2);
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: background var(--transition-fast);
    &:hover {
      background: var(--bg-hover);
    }
    &:has(input:checked) {
      background: var(--bg-subtle);
      font-weight: 500;
    }
  }
}

.ols-import__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-2);
}
</style>
